/**
 * DawPrescriptionList.tsx
 *
 * The patient Prescriptions tab's list of ScriptSure prescriptions (plan R6,
 * R7, R8): one entry per prescription with its medication, prescriber,
 * current status and how its latest send went. An entry whose latest send
 * failed stands out and says what to do. Each entry expands to its status
 * history and its pharmacy messages, fill notices included, and each message
 * opens in PrescriptionMessageViewer.
 *
 * Pharmacy messages the server could not tie to a prescription are listed
 * apart, under "Other pharmacy messages". The grouping, the status labels and
 * the outcome all come from the server (DawPrescriptionListService); this
 * only lays them out.
 *
 * It runs its own query under dawPrescriptionListKey, which the tab's Refresh
 * button, ScriptSure's '200' signal and the Prescribing page refresh. The tab
 * renders it keyed by patient, so a patient switch drops what was expanded
 * and closes an open message.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  PrescriptionMessageViewer,
  pharmacyMessageDirection,
  pharmacyMessageLabel,
} from '@/components/scriptsure/PrescriptionMessageViewer';
import { cn, formatDateTime } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  dawPrescriptionListKey,
  getDawPrescriptions,
  type DawPharmacyMessageSummary,
  type DawPrescriptionEntry,
  type DawSendOutcome,
} from '@/services/prescriptions.service';

const LOAD_FAILED_TEXT =
  "Couldn't load prescriptions. Try Refresh, and contact support if it keeps happening.";

/** What each send outcome says. Only an error asks the reader to act. */
const OUTCOME_TEXT: Record<DawSendOutcome['kind'], string> = {
  status: 'Sent to the pharmacy',
  verify: 'Pharmacy confirmed receipt',
  error: 'Not sent. Review it in ScriptSure and send it again.',
};

function Outcome({ outcome }: { outcome: DawSendOutcome }) {
  if (outcome.kind === 'error') {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-rose-700">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        {OUTCOME_TEXT.error}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
      {OUTCOME_TEXT[outcome.kind]}
    </span>
  );
}

function MessageRows({
  messages,
  onOpen,
}: {
  messages: DawPharmacyMessageSummary[];
  onOpen: (id: number) => void;
}) {
  return (
    <ul className="space-y-1">
      {messages.map((message) => {
        const direction = pharmacyMessageDirection(message.direction);
        return (
          <li key={message.id}>
            <button
              type="button"
              onClick={() => onOpen(message.id)}
              className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded px-2 py-1 text-left text-sm hover:bg-slate-100"
            >
              <span className="font-medium text-blue-700">{pharmacyMessageLabel(message.type)}</span>
              {direction && <span className="text-xs text-slate-500">{direction}</span>}
              <span className="text-xs text-slate-500">{formatDateTime(message.at)}</span>
              {message.hasSignature && (
                <span className="text-xs text-slate-500">Includes signature information</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Entry({
  entry,
  expanded,
  onToggle,
  onOpenMessage,
}: {
  entry: DawPrescriptionEntry;
  expanded: boolean;
  onToggle: () => void;
  onOpenMessage: (id: number) => void;
}) {
  const panelId = `daw-prescription-${entry.key}`;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <li
      data-error={entry.hasError ? 'true' : undefined}
      className={cn(entry.hasError && 'border-l-4 border-rose-500 bg-rose-50')}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
      >
        <Chevron className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-slate-900">
            {entry.medication || 'Medication not named'}
          </span>
          {entry.prescriber && <span className="block text-xs text-slate-500">{entry.prescriber}</span>}
          {entry.outcome && (
            <span className="mt-1 block text-xs">
              <Outcome outcome={entry.outcome} />
            </span>
          )}
        </span>
        <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
          {entry.status?.label ?? 'No status yet'}
        </span>
      </button>

      {expanded && (
        <div id={panelId} className="space-y-3 px-3 pb-3 pl-9">
          <div>
            <h3 className="text-xs font-medium text-slate-500">Status history</h3>
            {entry.history.length === 0 ? (
              <p className="text-sm text-slate-500">No status yet</p>
            ) : (
              <ol className="space-y-0.5">
                {entry.history.map((status, index) => (
                  <li key={`${status.label}-${status.at ?? ''}-${index}`} className="flex flex-wrap gap-x-2 text-sm">
                    <span className="text-slate-800">{status.label}</span>
                    <span className="text-xs text-slate-500">{formatDateTime(status.at)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div>
            <h3 className="text-xs font-medium text-slate-500">Pharmacy messages</h3>
            {entry.messages.length === 0 ? (
              <p className="text-sm text-slate-500">No pharmacy messages yet</p>
            ) : (
              <MessageRows messages={entry.messages} onOpen={onOpenMessage} />
            )}
          </div>
        </div>
      )}
    </li>
  );
}

interface DawPrescriptionListProps {
  patientId: number;
}

export function DawPrescriptionList({ patientId }: DawPrescriptionListProps) {
  const query = useQuery({
    queryKey: dawPrescriptionListKey(patientId),
    queryFn: () => getDawPrescriptions(patientId),
  });
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [openMessageId, setOpenMessageId] = useState<number | null>(null);

  function toggle(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const data = query.data;

  return (
    <div className="space-y-2">
      {query.isLoading && (
        <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading prescriptions
        </div>
      )}

      {query.isError && (
        <p className="p-4 text-sm text-rose-600">{getErrorMessage(query.error, LOAD_FAILED_TEXT)}</p>
      )}

      {data && data.prescriptions.length === 0 && (
        <p className="p-4 text-sm text-slate-500">No prescriptions yet</p>
      )}

      {data && data.prescriptions.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {data.prescriptions.map((entry) => (
            <Entry
              key={entry.key}
              entry={entry}
              expanded={expanded.has(entry.key)}
              onToggle={() => toggle(entry.key)}
              onOpenMessage={setOpenMessageId}
            />
          ))}
        </ul>
      )}

      {data && data.otherMessages.length > 0 && (
        <div className="space-y-1 pt-2">
          <h3 className="text-sm font-medium text-slate-700">Other pharmacy messages</h3>
          <MessageRows messages={data.otherMessages} onOpen={setOpenMessageId} />
        </div>
      )}

      <PrescriptionMessageViewer
        patientId={patientId}
        messageId={openMessageId}
        onClose={() => setOpenMessageId(null)}
      />
    </div>
  );
}
